import { unwrapResult } from "@reduxjs/toolkit";
import { LoadingButton } from "@mui/lab";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Localized } from "@fluent/react";
import { useCallback, useState } from "react";
import { DocumentSelectField } from "../../../documents/controls/DocumentSelectField";
import { DocumentHeader } from "../../../documents/documentsApi";
import { APIError, AppId } from "../../../../app/appTypes";
import { useAppDispatch } from "../../../../app/hooks";
import { ServerErrorMsg } from "../../../../app/ServerErrorMsg";
import { linkValidationProjectDocument, ValidationProjectDocumentDto } from "../validationProjectSlice";

export interface LinkValidationProjectDocumentDialogProps {
    projectId: AppId;
    onSave: (document: ValidationProjectDocumentDto) => void;
    onCancel: () => void;
}

export const LinkValidationProjectDocumentDialog = ({ projectId, onSave, onCancel }: LinkValidationProjectDocumentDialogProps) => {
    const dispatch = useAppDispatch();
    const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
    const [selectedDocument, setSelectedDocument] = useState<DocumentHeader | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<APIError | null>(null);

    const handleSaveClick = useCallback(() => {
        if (!selectedDocumentId) {
            return;
        }

        setSaving(true);
        setError(null);
        dispatch(linkValidationProjectDocument({
            id: projectId,
            payload: {
                documentId: Number(selectedDocumentId),
                order: null,
            },
        }))
            .then(unwrapResult)
            .then((document) => onSave(document))
            .catch((err: APIError) => setError(err))
            .finally(() => setSaving(false));
    }, [dispatch, onSave, projectId, selectedDocumentId]);

    return <Dialog open fullWidth maxWidth="md">
        <DialogTitle>
            <Localized id="validation-project-link-document">
                <span>Podlinkuj dokument</span>
            </Localized>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
            <DocumentSelectField
                documentType="URS"
                value={selectedDocumentId}
                selected={selectedDocument}
                defaultStatusFilter={["A", "P"]}
                onChange={(id, doc) => {
                    setSelectedDocumentId(id);
                    setSelectedDocument(doc ?? null);
                }}
            />
            <Box sx={{ mt: 2 }}>
                <ServerErrorMsg err={error} />
            </Box>
        </DialogContent>
        <DialogActions>
            <LoadingButton loading={saving} onClick={handleSaveClick} disabled={!selectedDocumentId}>
                <Localized id="save"><span>Zapisz</span></Localized>
            </LoadingButton>
            <LoadingButton loading={saving} onClick={onCancel}>
                <Localized id="cancel"><span>Anuluj</span></Localized>
            </LoadingButton>
        </DialogActions>
    </Dialog>;
};